/**
 * "Dashboard tour" — explains each card on the AML dashboard.
 *
 * Several cards are management-only (CO/SM/GB/Admin). Steps targeting those
 * cards are gated with `showOn: targetExists(...)` so they are silently
 * skipped for users who don't see those cards.
 *
 * Targets used (data-tour attributes on aml-card components):
 *   data-tour="dash-tasks"             — My Tasks
 *   data-tour="dash-recent-activity"   — Recent Activity
 *   data-tour="dash-escalations"       — Escalations
 *   data-tour="dash-client-risk"       — Client Risk Summary
 *   data-tour="dash-reporting"         — Reporting Deadlines (management)
 *   data-tour="dash-program-health"    — Program Health (management)
 *   data-tour="dash-training"          — Training Compliance (management)
 *   data-tour="dash-austrac"           — AUSTRAC Enrolment (management, conditional)
 */

import { NEXT, BACK, START, SKIP, DONE, targetExists } from './_helpers.js';

export const dashboardTour = {
    id: 'dashboard',
    title: 'Dashboard tour',
    description: 'Walks through each card on the AML dashboard so you understand what every section is showing you.',
    autoLaunchOn: { page: 'dashboard' },

    steps: [
        {
            id: 'intro',
            title: 'The AML dashboard',
            text: `This is your home view. Each card surfaces a different slice of your
                compliance picture — what needs your attention, what's healthy, and what's
                coming up. Let's walk through them.`,
            buttons: [START],
        },
        {
            id: 'getting-started',
            attachTo: { element: '[data-tour="dash-getting-started"]', on: 'bottom' },
            showOn: targetExists('[data-tour="dash-getting-started"]'),
            // The Getting Started card is full-width at the very top of the
            // dashboard. Shepherd's default `block: 'center'` would scroll
            // the page down to centre it, pushing the tooltip (placed below
            // the card) past the bottom of the viewport. Scroll to page top
            // instead so the card sits at the top and the tooltip has room.
            scrollTo: false,
            beforeShowPromise: () => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return new Promise(resolve => setTimeout(resolve, 250));
            },
            title: 'Getting Started checklist',
            text: `If your firm is new to the platform, this card walks you through
                the initial setup — entering your AUSTRAC enrolment, configuring
                your AML program, adding your first clients, and so on. Each step
                links straight to the relevant module.
                <br><br>
                The card disappears once every step is done, or you can dismiss it
                early with the <em>Dismiss</em> link in the top-right.
                <em>Visible to management roles only.</em>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'tasks',
            attachTo: { element: '[data-tour="dash-tasks"]', on: 'right' },
            showOn: targetExists('[data-tour="dash-tasks"]'),
            title: 'My Tasks',
            text: `Action items that are yours to handle right now — review reminders,
                approvals waiting on you, escalations assigned to you. You can click
                any task to jump straight to it.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'recent-activity',
            attachTo: { element: '[data-tour="dash-recent-activity"]', on: 'left' },
            showOn: targetExists('[data-tour="dash-recent-activity"]'),
            title: 'Recent Activity',
            text: `A rolling timeline of compliance events across your firm — onboarding
                completions, escalation status changes, training milestones, etc. Useful
                for a quick "what's been happening" glance.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'escalations',
            attachTo: { element: '[data-tour="dash-escalations"]', on: 'right' },
            showOn: targetExists('[data-tour="dash-escalations"]'),
            title: 'Escalations',
            text: `Compliance issues that have been flagged for review (PEP/sanctions
                hits, ID discrepancies, ongoing-monitoring concerns). Management see the
                full breakdown by status; staff see just the count assigned to them.
                <br><br>
                Anything <strong>stale &gt;48 hours</strong> shows a red banner — those
                need immediate attention.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'client-risk',
            attachTo: { element: '[data-tour="dash-client-risk"]', on: 'left' },
            showOn: targetExists('[data-tour="dash-client-risk"]'),
            title: 'Client Risk Summary',
            text: `Your client portfolio at a glance:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>High / Medium / Low risk</strong> counts.</li>
                    <li><strong>Reviews triggered</strong>, <strong>Reviews overdue</strong>,
                        and <strong>Due in 30 days</strong> — periodic-review pipeline.</li>
                    <li><strong>Incomplete CDD</strong> — clients whose due-diligence
                        file is still in progress.</li>
                    <li><strong>UBOs awaiting review</strong> and
                        <strong>UBO verifications outstanding</strong> — beneficial-owner
                        coverage.</li>
                    <li><strong>PEPs on file</strong> and any clients on
                        <strong>Sanctions or PEP Hold</strong>.</li>
                </ul>
                You can click any tile to jump to a pre-filtered Clients list.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'reporting',
            attachTo: { element: '[data-tour="dash-reporting"]', on: 'right' },
            showOn: targetExists('[data-tour="dash-reporting"]'),
            title: 'Reporting Deadlines',
            text: `Upcoming AUSTRAC report deadlines — including
                <strong>SMRs</strong> (Suspicious Matter Reports), <strong>TTRs</strong>
                (Threshold Transaction Reports), and <strong>CBMs</strong> (Cross-Border
                Movement reports). The card surfaces overdue counts, active SMR timers,
                and the next few items coming due. Anything overdue is highlighted red.
                <br><br>
                <em>Visible to management roles only.</em>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'program-health',
            attachTo: { element: '[data-tour="dash-program-health"]', on: 'left' },
            showOn: targetExists('[data-tour="dash-program-health"]'),
            title: 'Program Health',
            text: `Your AML/CTF program at a glance: current version, when the risk
                assessment was last reviewed, when the next program evaluation is due,
                ACR (Annual Compliance Report) status, and any pending program changes
                awaiting approval.
                <br><br>
                <em>Visible to management roles only.</em>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'training',
            attachTo: { element: '[data-tour="dash-training"]', on: 'right' },
            showOn: targetExists('[data-tour="dash-training"]'),
            title: 'Training Compliance',
            text: `Practice-wide training compliance — how many staff are up to date,
                in progress, or overdue, plus an overall completion percentage. The bar
                colour shifts based on whether your firm is on track.
                <br><br>
                <em>Visible to management roles only.</em>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'austrac',
            attachTo: { element: '[data-tour="dash-austrac"]', on: 'left' },
            showOn: targetExists('[data-tour="dash-austrac"]'),
            title: 'AUSTRAC Enrolment',
            text: `Your firm's AUSTRAC enrolment status, entity number, and any pending
                alerts from AUSTRAC. Reporting entities must be enrolled before providing
                designated services — this card flags it if you're not.
                <br><br>
                <em>Only appears once enrolment has been started in Settings.</em>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'finish',
            title: 'That\'s the dashboard',
            text: `You can replay this tour — or any other — from the <strong>Help</strong>
                page in the sidebar.`,
            buttons: [BACK, DONE],
        },
    ],
};
